<template>
  <Aggregate
    title="Storage"
    v-model="computedValue"
    :ItemConstructor="CaseModel.Storage"
    :headers="[
      { text: 'Name', value: 'name' },
      { text: 'Active', value: 'active', width: 1 },
    ]"
    :cardProps="{ flat: true, class: [] }"
    toolbar-dense
    dense
    @change="$emit('change')"
  >
    <template #[`item.active`]="{ item }">
      <v-switch v-model="item.active" dense @change="$emit('change')"></v-switch>
    </template>
    <template #[`item.capacity`]="{ item: storageItem }">
      <td>{{ storageItem.power_capacity[0] }} MW</td>
    </template>
    <template #default="{ item: storageItem, errors }">
      <v-expand-transition>
        <v-expansion-panels v-model="panels" tile multiple accordion flat class="mx-0 px-0">
          <v-expansion-panel :key="0">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              General
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <InputGeo
                v-model="storageItem.coordinates"
                label="Coordinates"
                map-height="150"
                nullable
                switch-text="Set coordinates"
                @change="$emit('change')"
              />
              <InputGeneric label="Inverter">
                <v-autocomplete
                  v-model="storageItem.inverter_id"
                  dense
                  :items="inverters"
                ></v-autocomplete>
              </InputGeneric>
            </v-expansion-panel-content>
            <v-divider />
          </v-expansion-panel>
          <v-expansion-panel :key="1">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              Capacity
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <InputYearly
                v-model="storageItem.power_capacity"
                :error-messages="$utils.getIfExists(errors, ['power_capacity'])"
                label="Rated Power Capacity"
                suffix="MW"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                spread-values
                @change="$emit('change')"
              ></InputYearly>
              <InputYearly
                v-model="storageItem.energy_capacity"
                label="Energy Capacity"
                suffix="MWh"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                spread-values
                @change="$emit('change')"
              ></InputYearly>
              <InputYearly
                v-model="storageItem.max_charge_power"
                :label="schema.max_charge_power.title"
                :suffix="schema.max_charge_power.unit"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                spread-values
                @change="$emit('change')"
              ></InputYearly>
              <InputYearly
                v-model="storageItem.max_discharge_power"
                :label="schema.max_discharge_power.title"
                :suffix="schema.max_discharge_power.unit"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                spread-values
                @change="$emit('change')"
              ></InputYearly>
            </v-expansion-panel-content>
            <v-divider />
          </v-expansion-panel>
          <v-expansion-panel :key="2">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              Characteristics
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <InputTextField
                v-model.number="storageItem.start_year"
                label="Start Year"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="storageItem.charging_efficiency"
                :error-messages="$utils.getIfExists(errors, ['charging_efficiency', 'messages'])"
                label="Charging Efficiency"
                suffix="%"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="storageItem.discharging_efficiency"
                :error-messages="$utils.getIfExists(errors, ['discharging_efficiency', 'messages'])"
                label="Discharging Efficiency"
                suffix="%"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="storageItem.ramp_rate"
                label="Ramping Rate"
                hint="Speed of charge or discharge change"
                suffix="MW/h"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="storageItem.initial_energy_throughput"
                suffix="MWh"
                hint="Initial cumulative energy cycled through BESS"
                :error-messages="
                  $utils.getIfExists(errors, ['initial_energy_throughput', 'messages'])
                "
                label="Initial Energy Throughput"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="storageItem.initial_cycle_count"
                hint="Starting number of BESS charge-discharge cycles"
                :error-messages="$utils.getIfExists(errors, ['initial_cycle_count', 'messages'])"
                label="Initial Cycle Count"
                suffix="cycles"
                type="number"
                @change="$emit('change')"
              ></InputTextField>

              <InputTextField
                v-model.number="storageItem.initial_soc"
                label="Initial SOC"
                hint="Starting state of charge of BESS/BESS Service"
                suffix="%"
                type="number"
                @change="socMinChange(storageItem)"
              ></InputTextField>

              <InputTextField
                v-model.number="storageItem.soc_min"
                :error-messages="$utils.getIfExists(errors, ['soc_min', 'messages'])"
                label="SOC Min"
                suffix="%"
                type="number"
                @change="socMinChange(storageItem)"
              ></InputTextField>
              <InputTextField
                v-model.number="storageItem.soc_max"
                :error-messages="$utils.getIfExists(errors, ['soc_max', 'messages'])"
                label="SOC Max"
                suffix="%"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model="storageItem.pa_relaxation_penalty_price"
                :label="schema.pa_relaxation_penalty_price.title"
                type="number"
                :suffix="schema.pa_relaxation_penalty_price.unit"
                nullable
                @change="$emit('change')"
              >
              </InputTextField>
              <InputProfile
                v-model="storageItem.capacity_fade"
                label="Capacity Fade Curve"
                profile-type="CAPACITY_FADE"
                object-value
                :use-headers="['cycle_limit', 'capacity_fade']"
                @change="$emit('change')"
              ></InputProfile>
              <v-row align="baseline">
                <v-col cols="12" sm="4">
                  <v-card class="d-flex" flat>
                    <v-card height="32" flat>
                      <v-switch
                        v-model="storageItem.cycling_limits"
                        dense
                        class="mt-0"
                        @change="cyclingLimitsChange(storageItem)"
                      ></v-switch>
                    </v-card>
                    <v-card flat>
                      <span
                        :class="{
                          'subtitle-1': true,
                          'text--secondary': !storageItem.cycling_limits,
                        }"
                      >
                        {{ schema.cycling_limits.title }}
                      </span>
                      <div>
                        <span class="text-caption">{{ schema.annual_cycling_limits.tooltip }}</span>
                      </div>
                    </v-card>
                  </v-card>
                </v-col>
                <v-col v-if="storageItem.cycling_limits" class="grow">
                  <InputYearly
                    v-model="storageItem.annual_cycling_limits"
                    type="number"
                    :suffix="schema.annual_cycling_limits.unit"
                    :num_years="num_years"
                    :year_start="year_start"
                    spread-values
                    fill
                    :fill-value="schema.annual_cycling_limits.items.default"
                    @change="$emit('change')"
                  ></InputYearly>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
            <v-divider />
          </v-expansion-panel>
          <v-expansion-panel :key="3">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              Costs
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <InputYearly
                v-model="storageItem.vom_charge"
                label="Charging VOM"
                suffix="$/MWh"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                fill
                :fill-value="0"
                @change="$emit('change')"
              ></InputYearly>
              <InputYearly
                v-model="storageItem.vom_discharge"
                label="Discharging VOM"
                suffix="$/MWh"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                fill
                :fill-value="0"
                @change="$emit('change')"
              ></InputYearly>
              <InputYearly
                v-if="storageItem.mcc"
                v-model="storageItem.mcc.profile"
                :label="schema.mcc.title"
                :suffix="schema.mcc.unit"
                type="number"
                :num_years="num_years"
                :year_start="year_start"
                fill
                :fill-value="0"
                @change="$emit('change')"
              >
                <template #inputPrepend>
                  <v-select
                    v-model="storageItem.mcc.type"
                    label="MCC Type"
                    :items="schema.mcc.properties.type.enum"
                    @change="$emit('change')"
                  ></v-select>
                </template>
              </InputYearly>

              <!-- PPAs -->
              <v-switch
                v-model="storageItem.ppa.active"
                :label="schema.ppa.title"
                :hint="schema.ppa.tooltip"
                persistent-hint
                dense
                @change="$emit('change')"
              ></v-switch>
              <InputYearlyProfile
                v-if="storageItem.ppa.active"
                v-model="storageItem.ppa.charging"
                object-value
                fill
                toggle-items-type
                :label="schema.ppa.properties.charging.title"
                :hint="schema.ppa.properties.charging.tooltip"
                :num_years="num_years"
                suffix="$/MWh"
                :profile-type="schema.ppa.properties.charging.profileType"
                @change="$emit('change')"
              ></InputYearlyProfile>
              <InputYearlyProfile
                v-if="storageItem.ppa.active"
                v-model="storageItem.ppa.discharging"
                object-value
                fill
                toggle-items-type
                :label="schema.ppa.properties.discharging.title"
                :hint="schema.ppa.properties.discharging.tooltip"
                suffix="$/MWh"
                :num_years="num_years"
                :profile-type="schema.ppa.properties.discharging.profileType"
                @change="$emit('change')"
              ></InputYearlyProfile>
              <InputYearlyProfile
                v-if="storageItem.ppa.active"
                v-model="storageItem.ppa.capacity"
                object-value
                fill
                toggle-items-type
                :label="schema.ppa.properties.capacity.title"
                :hint="schema.ppa.properties.capacity.tooltip"
                suffix="$/MW-month"
                :num_years="num_years"
                :profile-type="schema.ppa.properties.capacity.profileType"
                @change="$emit('change')"
              ></InputYearlyProfile>
            </v-expansion-panel-content>
            <v-divider />
          </v-expansion-panel>
          <v-expansion-panel :key="4">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              Operation
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <InputToggleParam
                v-model="storageItem.operating_status"
                :label="schema.operating_status.properties.active.title"
                :hint="schema.operating_status.properties.active.tooltip"
                :error-messages="$utils.getIfExists(errors, ['operating_status'])"
                @change="$emit('change')"
                hide-divider
              >
                <template v-slot="{ param, errors }">
                  <InputProfile
                    v-model="param.id"
                    :hint="schema.operating_status.then.properties.param.properties.id.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['id'])"
                    profile-type="DAILY_OPERATING_STATUS"
                    @change="$emit('change')"
                  ></InputProfile>
                </template>
              </InputToggleParam>
            </v-expansion-panel-content>
            <v-divider />
          </v-expansion-panel>
          <v-expansion-panel :key="5">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              <span>
                Services
                <span class="font-weight-regular">
                  ({{ activeServicesText(storageItem.services) }})
                </span>
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <SystemProjectServices
                v-model="storageItem.services"
                :year_start="year_start"
                :num_years="num_years"
                :project-demands="demands"
                :project-reserves="reserves"
                :project-generators="generators"
                resource-type="storage"
                @change="$emit('change')"
              />
            </v-expansion-panel-content>
            <v-divider></v-divider>
          </v-expansion-panel>
          <v-expansion-panel :key="6" v-if="storageItem.reactive_services">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-bold">
              <span>
                Reactive Services
                <span class="font-weight-regular">
                  ({{ activeServicesText(storageItem.reactive_services) }})
                </span>
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-switch
                v-model="storageItem.reactive_services.flicker_control.active"
                dense
                label="Flicker Control"
                hint="Reduce voltage flicker using reactive power support"
                @change="$emit('change')"
              ></v-switch>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-expand-transition>
    </template>
  </Aggregate>
</template>

<script>
import { CaseModel, schemas } from "data-dictionary";
import plur from "plur";

export default {
  name: "SystemProjectStorage",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    SystemProjectServices: () => import("@/components/SystemProjectServices.vue"),
    InputGeneric: () => import("@/components/inputs/InputGeneric.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
    InputToggleParam: () => import("@/components/InputToggleParam.vue"),
    InputProfile: () => import("@/components/InputProfile.vue"),
  },
  mixins: [],
  props: {
    value: { type: Array, required: true },
    year_start: { type: Number, required: true },
    num_years: { type: Number, required: true },
    project: { type: Object, required: true },
    demands: { type: Array, required: true },
    reserves: { type: Array, required: true },
    generators: { type: Array, required: true },
    errors: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      CaseModel,
      schema: schemas.find((v) => v.$id === "storage_project.schema.json").properties,
      panels: [0, 1, 2, 3],
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
    inverters() {
      return this.project.inverters
        ? this.project.inverters.map((v) => ({ text: `${v.name} (${v.id})`, value: v.id }))
        : [];
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    socMinChange(storageItem) {
      const valueCopy = this.value;
      if (storageItem.initial_soc < storageItem.soc_min) {
        valueCopy[storageItem.id - 1].initial_soc = storageItem.soc_min;
      }
      this.$emit("input", valueCopy);
      this.$emit("change");
    },
    cyclingLimitsChange(storageItem) {
      const valueCopy = this.value;
      if (!storageItem.cycling_limits) {
        valueCopy[storageItem.id - 1].annual_cycling_limits = [];
      }
      this.$emit("input", valueCopy);
      this.$emit("change");
    },
    activeServicesText(servicesObj) {
      const count = Object.values(servicesObj).filter((v) => v.active).length;
      return `${count} ${plur("service", count)} enabled`;
    },
  },
};
</script>

<style></style>
